import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { TDR } from 'tdr-common';
import Check from '../../icons/green-check-circle.svg';
import { isParamEventSelectedByFilters } from '../../helpers/matchingEvents';
import { useEventBookNowOnResults } from '../../hooks/useEventBookNowOnResults';
import { SearchFilters } from '../../routes/AvailabilityGuard';
import Spinner from '../Spinner';

type EventBookNowOnResultsButtonProps = {
	event: TDR.Event;
	tables: TDR.Table[];
	restaurantId: string;
	timezone?: string | null;
	currentFilters: SearchFilters;
	updateSearchParams: (filters: Partial<SearchFilters>) => void;
	className?: string;
	disabledClassName?: string;
	tabIndex?: number;
	onClick?: React.MouseEventHandler;
	enabled?: boolean;
	label?: string;
	selectedLabel?: string;
	selectedClassName?: string;
	noAvailabilityLabel?: string;
};

export const EventBookNowOnResultsButton = ({
	event,
	tables,
	restaurantId,
	timezone,
	currentFilters,
	updateSearchParams,
	className,
	disabledClassName,
	tabIndex = 0,
	onClick,
	enabled = true,
	label,
	selectedLabel,
	selectedClassName,
	noAvailabilityLabel
}: EventBookNowOnResultsButtonProps) => {
	const { t } = useTranslation();
	const [clickedLoading, setClickedLoading] = useState(false);

	const {
		searchFilters,
		isResolved,
		isLoading,
		hasNoAvailability
	} = useEventBookNowOnResults(event, tables, restaurantId, timezone, currentFilters, { enabled });

	const isSelected = isParamEventSelectedByFilters(event, currentFilters, timezone ?? undefined);
	const selectEventText = label ?? t('actions.select_event', { defaultValue: 'Select Event' });
	const selectedText = selectedLabel ?? t('actions.selected', { defaultValue: 'Selected' });
	const noAvailabilityText = noAvailabilityLabel ?? t('inventory.sold_out', { defaultValue: 'Sold out' });
	const filtersUpdatedText = t('messages.filters_updated_for_event', {
		defaultValue: 'Filters updated to align with event times'
	});

	const applyEventFilters = (filters: Partial<SearchFilters>) => {
		updateSearchParams(filters);
		toast.success(filtersUpdatedText, { toastId: 'event-filters-updated' });
	};

	useEffect(() => {
		if (!clickedLoading || !isResolved) {
			return;
		}
		setClickedLoading(false);
		if (searchFilters) {
			applyEventFilters(searchFilters);
		}
	}, [clickedLoading, isResolved, searchFilters, updateSearchParams, filtersUpdatedText]);

	if (!enabled) {
		return null;
	}

	if (isSelected) {
		return (
			<span
				className={`${selectedClassName ?? className ?? ''} inline-flex flex-row items-center gap-1.5 text-green-500 whitespace-nowrap [text-decoration:none]`}
				style={{ textDecoration: 'none' }}
				data-testid="event-selected-label"
				aria-current="true"
			>
				<img src={Check} alt="" className="w-4 h-4 shrink-0" aria-hidden="true" />
				<span className="leading-none">{selectedText}</span>
			</span>
		);
	}

	if (isResolved && hasNoAvailability) {
		return (
			<button
				className={disabledClassName ?? className}
				type="button"
				disabled
				tabIndex={tabIndex}
				aria-disabled="true"
			>
				{noAvailabilityText}
			</button>
		);
	}

	if (clickedLoading && isLoading) {
		return (
			<button
				className={className}
				type="button"
				disabled
				tabIndex={tabIndex}
				aria-busy="true"
				aria-label={selectEventText}
			>
				<Spinner size="xs" />
			</button>
		);
	}

	if (searchFilters) {
		const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
			onClick?.(e);
			if (e.defaultPrevented) {
				return;
			}
			applyEventFilters(searchFilters);
		};

		return (
			<button
				className={className}
				type="button"
				tabIndex={tabIndex}
				onClick={handleClick}
				data-testid="event-select-btn"
			>
				{selectEventText}
			</button>
		);
	}

	const handlePendingClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		onClick?.(e);
		if (e.defaultPrevented) {
			return;
		}
		setClickedLoading(true);
	};

	return (
		<button
			className={className}
			type="button"
			tabIndex={tabIndex}
			onClick={handlePendingClick}
			data-testid="event-select-btn"
		>
			{selectEventText}
		</button>
	);
};
